import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { IMaskInput } from 'react-imask';

import '@src/css/components/_select-chevron.scss';

/**
 * ControlledInput - Controlled form input supporting text, select, and masked types
 *
 * Used for simple forms with native React state management.
 * For React Hook Form, use the TextInput component from FormInputs instead.
 */
const ControlledInput = ({
    label,
    name,
    value,
    type = 'text',
    required = false,
    placeholder = '',
    className = 'w-100',
    options = [],
    defaultOptionText = '',
    onChange,
    disabled = false,
    readOnly = false,
    isInvalid = false,
    feedback = '',
    maxLength = null,
    controlId = null,
    groupClassName = '',
    mask = null,
}) => {
    const renderInput = () => {
        if (mask) {
            return (
                <IMaskInput
                    id={controlId || name}
                    required={required}
                    mask={mask}
                    name={name}
                    value={value}
                    placeholder={placeholder}
                    className={classNames('form-control', className, {
                        'is-invalid': isInvalid,
                    })}
                    disabled={disabled}
                    readOnly={readOnly}
                    onAccept={(val) => {
                        onChange({ target: { name, value: val } });
                    }}
                    onChange={onChange}
                />
            );
        }

        if (type === 'select') {
            return (
                <div className="select-chevron">
                    <Form.Control
                        required={required}
                        as="select"
                        name={name}
                        value={value}
                        className={className}
                        disabled={disabled}
                        readOnly={readOnly}
                        isInvalid={isInvalid}
                        onChange={onChange}
                    >
                        {defaultOptionText && (
                            <option key="blank-option" value="">
                                {defaultOptionText}
                            </option>
                        )}
                        {options.map((option) => (
                            <option key={`option-${option.id}`} value={option.id}>
                                {option.title}
                            </option>
                        ))}
                    </Form.Control>
                </div>
            );
        }

        return (
            <Form.Control
                required={required}
                type={type}
                name={name}
                value={value}
                placeholder={placeholder}
                className={className}
                disabled={disabled}
                readOnly={readOnly}
                isInvalid={isInvalid}
                maxLength={maxLength}
                onChange={onChange}
            />
        );
    };

    return (
        <Form.Group
            controlId={controlId || name}
            className={classNames('mb-3 w-100 floating-label', {
                'disabled-field': disabled || readOnly,
            }, groupClassName)}
        >
            <Form.Label>
                { required && <abbr title="required">*</abbr> }
                {' '}
                { label }
            </Form.Label>
            {renderInput()}
            {feedback && (
                <Form.Control.Feedback type="invalid">
                    {feedback}
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

ControlledInput.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.oneOf(['text', 'select', 'email', 'tel', 'number']),
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            title: PropTypes.string.isRequired,
        }),
    ),
    defaultOptionText: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    isInvalid: PropTypes.bool,
    feedback: PropTypes.node,
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    controlId: PropTypes.string,
    groupClassName: PropTypes.string,
    mask: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
};

export default React.memo(ControlledInput);

